import { Component, OnDestroy, OnInit } from '@angular/core';
import { ENDPOINTS } from '@core/constants/endpoints';
import { LoadingService } from '@core/services/loading.service';
import { lastValueFrom, Subscription } from 'rxjs';
import { VulnerabilitiesService } from '@app/pages/services/vulnerabilities.service';
import { Vulnerabilities } from '@app/shared/models/vulnerabilities.model';
import { EMPTY_STATE_TYPE } from '@app/shared/enum/empty-state';
import { TranslateService } from '@ngx-translate/core';
import { InternationalizationService } from '@core/services/internationalization.service';
import { take } from 'rxjs/operators';
import { get } from 'lodash';
import { WindowRefService } from '@app/core/services/helpers/window-ref.service';

@Component({
    selector: 'app-vulnerabilities',
    templateUrl: './vulnerabilities.component.html',
    styleUrls: ['./vulnerabilities.component.scss'],
    standalone: false,
})
export class VulnerabilitiesComponent implements OnInit, OnDestroy {
    vulnerabilities: Vulnerabilities;
    isEmpty = false;
    emptyState: {
        type: EMPTY_STATE_TYPE;
        title: string;
        message: string;
    };

    private languageSubscription: Subscription;

    constructor(
        private vulnerabilitiesService: VulnerabilitiesService,
        private windowRefService: WindowRefService,
        private translateService: TranslateService,
        private internationalizationService: InternationalizationService,
        public loadingService: LoadingService
    ) {}

    ngOnInit(): void {
        this.languageSubscription = this.internationalizationService.currentLanguage.subscribe(
            (language) => {
                this.setTexts();
                this.getData(language.lang).catch(() => {});
            }
        );
    }

    redirectToHelpCenter() {
        this.windowRefService.window.open(ENDPOINTS.helpCenter.base, '_blank');
    }

    openResourceLink(link: string) {
        this.windowRefService.window.open(link, '_blank');
    }

    private async getData(lang: string) {
        this.loadingService.startLoading(this);

        try {
            this.vulnerabilities = await lastValueFrom(
                this.vulnerabilitiesService.getVulnerabilities(lang)
            );
        } finally {
            this.isEmpty =
                !this.vulnerabilities?.active.length && !this.vulnerabilities?.archived.length;
            this.loadingService.endLoading(this);
        }
    }

    private setTexts(): void {
        this.translateService
            .get('PAGES.VULNERABILITIES')
            .pipe(take(1))
            .subscribe((vulnerabilitiesTranslations) => {
                this.emptyState = {
                    type: EMPTY_STATE_TYPE.EMPTY_COLLECTION,
                    title: get(vulnerabilitiesTranslations, 'EMPTY_STATE.TITLE', '') as string,
                    message: get(vulnerabilitiesTranslations, 'EMPTY_STATE.MESSAGE', '') as string,
                };
            });
    }

    ngOnDestroy() {
        this.languageSubscription.unsubscribe();
    }
}
